import { AudioBus, type AudioBackend, type AudioClip, type AudioPlayback, type PlaybackRequest } from './AudioBackend';

const RUNNING_STATE = 'running';

/** A clip backed by a decoded Web Audio buffer. */
class WebAudioClip implements AudioClip {
  readonly buffer: AudioBuffer;

  constructor(buffer: AudioBuffer) {
    this.buffer = buffer;
  }

  /** Length of the sound in seconds. */
  get durationSeconds(): number {
    return this.buffer.duration;
  }
}

/** One running source node, removed from the backend's live set when it ends. */
class WebAudioPlayback implements AudioPlayback {
  readonly #source: AudioBufferSourceNode;

  #playing = true;

  constructor(source: AudioBufferSourceNode, onEnded: () => void) {
    this.#source = source;
    source.addEventListener('ended', () => {
      this.#playing = false;
      onEnded();
    });
  }

  /** Whether the source node is still running. */
  get isPlaying(): boolean {
    return this.#playing;
  }

  /** Stops the source node. The `ended` event does the bookkeeping. */
  stop(): void {
    if (!this.#playing) {
      return;
    }
    this.#source.stop();
  }
}

/** Construction options for {@link WebAudioBackend}. */
export interface WebAudioBackendOptions {
  /** Overrides the context, normally so a host can share one it already owns. */
  readonly context?: AudioContext;
}

/**
 * The Web Audio implementation of {@link AudioBackend}.
 *
 * The graph is three gain nodes deep and no deeper: every source connects to its bus gain, both
 * gameplay buses connect to the master gain, and the master connects to the destination. That is the
 * whole reason buses exist, and it means a volume change is one assignment rather than a walk over
 * live sounds.
 *
 * Browsers start a context suspended until a user gesture, which is why {@link WebAudioBackend.unlock}
 * exists and why nothing here assumes it can make noise before it is called.
 */
export class WebAudioBackend implements AudioBackend {
  readonly #context: AudioContext;
  readonly #masterGain: GainNode;
  readonly #busGains: ReadonlyMap<AudioBus, GainNode>;
  readonly #live = new Set<AudioBufferSourceNode>();

  #disposed = false;

  constructor(options: WebAudioBackendOptions = {}) {
    this.#context = options.context ?? new AudioContext();

    this.#masterGain = this.#context.createGain();
    this.#masterGain.connect(this.#context.destination);

    const sfxGain = this.#context.createGain();
    const musicGain = this.#context.createGain();
    sfxGain.connect(this.#masterGain);
    musicGain.connect(this.#masterGain);

    this.#busGains = new Map([
      [AudioBus.Master, this.#masterGain],
      [AudioBus.Sfx, sfxGain],
      [AudioBus.Music, musicGain],
    ]);
  }

  /** Whether the context is running rather than suspended. */
  get isUnlocked(): boolean {
    return this.#context.state === RUNNING_STATE;
  }

  /** Resumes the context. Must be reached from a user gesture for the browser to allow it. */
  async unlock(): Promise<void> {
    if (this.#disposed || this.isUnlocked) {
      return;
    }
    await this.#context.resume();
  }

  /** Decodes encoded audio bytes. The bytes are consumed and must not be reused. */
  async decode(bytes: ArrayBuffer): Promise<AudioClip> {
    return new WebAudioClip(await this.#context.decodeAudioData(bytes));
  }

  /** Applies a bus' effective gain. */
  setBusGain(bus: AudioBus, gain: number): void {
    const node = this.#busGains.get(bus);
    if (node === undefined) {
      return;
    }
    node.gain.value = gain;
  }

  /** Starts a clip on its bus. Source nodes are single use, so one is created per play. */
  play(clip: AudioClip, request: PlaybackRequest): AudioPlayback {
    if (!(clip instanceof WebAudioClip)) {
      throw new Error('This clip was not created by WebAudioBackend.decode.');
    }

    const source = this.#context.createBufferSource();
    source.buffer = clip.buffer;
    source.loop = request.loop;
    source.playbackRate.value = request.rate;

    const gain = this.#context.createGain();
    gain.gain.value = request.volume;
    source.connect(gain);
    gain.connect(this.#busGains.get(request.bus) ?? this.#masterGain);

    this.#live.add(source);
    const playback = new WebAudioPlayback(source, () => {
      gain.disconnect();
      this.#live.delete(source);
    });
    source.start();
    return playback;
  }

  /** Stops every live source. Each one's `ended` event removes it from the live set. */
  stopAll(): void {
    for (const source of [...this.#live]) {
      source.stop();
    }
  }

  /** Stops everything and closes the context. */
  dispose(): void {
    if (this.#disposed) {
      return;
    }
    this.#disposed = true;

    this.stopAll();
    this.#live.clear();
    void this.#context.close();
  }
}

/**
 * Whether this environment can play Web Audio at all.
 *
 * The one place that asks the question. {@link AudioManager} falls back to
 * {@link SilentAudioBackend} when the answer is no, so no game and no service above the backend needs
 * a "do we have audio" branch.
 */
export function isWebAudioAvailable(): boolean {
  return typeof AudioContext !== 'undefined';
}
